import { useDebounceCallback } from '@react-hook/debounce';
import * as React from 'react';
import {
  AudioPlayerInstance,
  AudioPlayerState,
  Membership,
  PlaybackItem,
  Playlist,
  StandaloneAudioPlayerType,
} from '../../types';
import { arraySample, getTotalPlayedTime } from '../../utils/common';
import { isAndroid, logPlayerEvent, safeAudioPlay } from '../../utils/frontend';
import StandaloneAudioPlayerContext from './StandaloneAudioPlayerContext';
import MembershipPopupContext from '../../contexts/MembershipPopupContext';
import {
  loggedInNonMemberTimeLimit,
  nonLoggedInUserTimeLimit,
  allRecordingsFree,
  isDev,
  frontendHost,
  wpHost,
} from '../../../config';
import { parseLameHeader } from '../../utils/lameHeader';
import FirebaseContext from '../../contexts/FirebaseContext';
import useIsPortfolio from '../../hooks/useIsPortfolio';

const playerDataStorageKey = 'earth.fm:audio-player:data';
const playerWasShownStorageKey = 'earth.fm:audio-player:was-shown';
const portfolioSessionKey = 'earth.fm:portfolio-session';

// Gapless looping decodes a whole channel into memory before any sound: 0.35MB and roughly a tenth
// of a second of decoding per second of audio. Android runs out of both (a 16 minute channel takes
// minutes there), so stations on Android stream through a pair of players instead.
const playsThroughWebAudio = (item: PlaybackItem | null | undefined) =>
  !!item?.isRadio && !isAndroid;

// Android station loop. An MP3 carries a sliver of encoder silence at each end, so neither end is
// played: the outgoing player is faded out before the tail and the incoming one starts past the
// head. The crossfade covers the join, and the incoming player is positioned ahead of time so
// nothing has to seek when the loop comes round.
const stationLoopHeadSkip = 0.05;
const stationLoopTailSkip = 0.15;
const stationLoopCrossfade = 0.4;
const stationLoopPrepareLead = 8;
const stationLoopPollInterval = 250;
const stationLoopFadeSteps = 16;

const stopRadioSource = (source: AudioBufferSourceNode | null | undefined, when?: number) => {
  try {
    source?.stop(when);
  } catch {
    // ignore: stop() throws if the node was never started
  }
};

// Local dev only: gatsby-source-wordpress searchAndReplace rewrites wpHost → frontendHost
// in all string fields, including audioUrl. This reverses that rewrite so local dev can
// fetch radio recordings that haven't been offloaded to the CDN yet.
function resolveAudioSourceUrl(sourceUrl: string): string {
  if (!isDev) {
    return sourceUrl;
  }
  const devPrefix = `http://${frontendHost}/`;
  if (sourceUrl.startsWith(devPrefix)) {
    const resolved = `http://${wpHost}/${sourceUrl.slice(devPrefix.length)}`;
    return resolved;
  }
  return sourceUrl;
}

interface Options {
  syncInLocalStorage?: boolean;
  defaultPlaybackItem?: PlaybackItem | null;
}

export default function useAudioPlayer({
  syncInLocalStorage = true,
  defaultPlaybackItem = null,
}: Options = {}): AudioPlayerInstance {
  const [state, setState] = React.useState<AudioPlayerState>('idle');
  const [playlist, setPlaylist] = React.useState<Playlist | null>(null);
  const [currentItem, setCurrentItem] = React.useState<PlaybackItem | null>(null);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [isShuffling, setIsShuffling] = React.useState(false);
  const [isLooping, setIsLooping] = React.useState(false);
  const [isPreviewMode, setIsPreviewMode] = React.useState(false);
  const [previewTimeRemaining, setPreviewTimeRemaining] = React.useState(0);
  const [isPlaybackFromPortfolio, setIsPlaybackFromPortfolio] = React.useState(false);

  const isStation = !!currentItem?.isRadio;
  const isStreamedStation = isStation && isAndroid;
  const isWebAudioStation = playsThroughWebAudio(currentItem);
  const isStationRef = React.useRef(isStation);
  isStationRef.current = isStation;
  const { currentlyPlayingItem, setCurrentlyPlayingItem, isStandaloneAudioPlayerPlaying } =
    React.useContext<StandaloneAudioPlayerType>(StandaloneAudioPlayerContext);

  const { user, membership } = React.useContext(FirebaseContext);
  const isPortfolio = useIsPortfolio();

  const { showMembershipModal } = React.useContext(MembershipPopupContext);
  // Using ref to so that it doesn't trigger auto play when used play callback
  const showMembershipRef = React.useRef(showMembershipModal);
  React.useEffect(() => {
    showMembershipRef.current = showMembershipModal;
  }, [showMembershipModal]);
  const freeTimeLimitReachedRef = React.useRef(false);

  // reset freeTimeLimitReachedRef when user becomes a member
  React.useEffect(() => {
    if (membership !== Membership.None) {
      freeTimeLimitReachedRef.current = false;
    }
  }, [membership]);

  const audioRef = React.useRef<HTMLAudioElement>();

  // Web Audio station refs (gapless looping via AudioBufferSourceNode)
  const audioContextRef = React.useRef<AudioContext | null>(null);
  const gainNodeRef = React.useRef<GainNode | null>(null);
  const radioStreamDestRef = React.useRef<MediaStreamAudioDestinationNode | null>(null);
  const radioUnlockAudioRef = React.useRef<HTMLAudioElement | null>(null);
  const keepRadioAudioOnSwitchRef = React.useRef(false);
  const radioSourceNodeRef = React.useRef<AudioBufferSourceNode | null>(null);
  const radioBufferRef = React.useRef<AudioBuffer | null>(null);
  // The pair of players a streamed station loops through, and the fade that hands over between
  // them. `active` is the audible one and is mirrored into audioRef so the volume control reaches
  // it. They swap roles at every join.
  const stationPlayersRef = React.useRef<{
    active: HTMLAudioElement;
    standby: HTMLAudioElement;
  } | null>(null);
  const stationFadeRef = React.useRef<NodeJS.Timeout | null>(null);
  // A station is left running for hours, so the channel is kept in memory as the file itself
  // (7-22MB) rather than re-read from the network on every loop. Not the decoded audio, which
  // is what the gapless path holds and what Android cannot afford.
  const stationSourceUrlRef = React.useRef('');
  const stationBlobUrlRef = React.useRef<string | null>(null);
  const stationBlobAbortRef = React.useRef<AbortController | null>(null);
  const stationStandbyReadyRef = React.useRef(false);
  const stationStandbyRetriedRef = React.useRef(false);
  const pauseRef = React.useRef<() => void>(() => {});
  const radioLoopStartRef = React.useRef(0);
  const radioLoopEndRef = React.useRef(0);
  const radioPlayStartTimeRef = React.useRef(0); // audioContext.currentTime when playback began
  const radioFadeOutEndTimeRef = React.useRef(0); // audioContext.currentTime when pending fade-out ends
  // Ref so visibilitychange / statechange handlers always read the current play state
  // without needing to be re-registered on every state change.
  const shouldResumeRadioRef = React.useRef(false);
  const autoPlayOnLoad = React.useRef(true);
  const initialAudioPosition = React.useRef(0);
  const playStartEventLogged = React.useRef(false);
  const playEventSource = React.useRef<string | null>(null);
  const previewTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const fadeIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const originalVolumeRef = React.useRef(1);
  const userVolumeRef = React.useRef(1); // GainNode target for radio fade-in; updated by VolumeControl
  const playlistRef = React.useRef<Playlist | null>(null);
  const currentItemIdRef = React.useRef<number | null>(null);
  const prevCurrentItemRef = React.useRef<PlaybackItem | null>(null);

  const totalPlayedTimeRef = React.useRef(getTotalPlayedTime().totalPlayed);
  // Make sure we call getTotalPlayedTime only once every 5 seconds for performance reasons
  const checkFreeTimeLimit = React.useMemo(() => Math.round(currentTime) % 5 === 0, [currentTime]);

  // Preview mode helpers
  const clearPreviewTimers = React.useCallback(() => {
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = null;
    }
  }, []);

  // Determine if current item should be in preview mode
  const shouldEnterPreviewMode = React.useMemo(() => {
    // If playback was initiated from portfolio page, never enter preview mode
    if (isPlaybackFromPortfolio) {
      return false;
    }
    const result =
      membership === Membership.None &&
      currentItem &&
      !allRecordingsFree &&
      currentItem.isFree === false;
    return result;
  }, [membership, currentItem, isPlaybackFromPortfolio]);

  React.useEffect(() => {
    totalPlayedTimeRef.current = getTotalPlayedTime().totalPlayed;
    const timeLimit = user ? loggedInNonMemberTimeLimit : nonLoggedInUserTimeLimit;
    if (
      totalPlayedTimeRef.current > timeLimit &&
      membership === Membership.None &&
      !isPlaybackFromPortfolio &&
      audioRef.current &&
      !audioRef.current.paused
    ) {
      freeTimeLimitReachedRef.current = true;
      showMembershipRef.current('free-limit-reached');
      audioRef.current?.pause();
    } else {
      freeTimeLimitReachedRef.current = false;
    }
  }, [checkFreeTimeLimit, membership, user, state, isPlaybackFromPortfolio]);

  const loadLastPlaybackItem = React.useCallback(() => {
    if (!syncInLocalStorage) {
      return false;
    }

    if (typeof window === 'undefined') {
      return false;
    }

    if (currentItem) {
      return false; // return if current item is already set
    }

    const dataStr = localStorage.getItem(playerDataStorageKey);
    // Always remove immediately so re-triggering this function (e.g. after reset())
    // never re-loads the same entry a second time.
    localStorage.removeItem(playerDataStorageKey);
    if (dataStr) {
      try {
        const data: {
          playlist?: Playlist | null;
          currentItem?: PlaybackItem | null;
          currentTime?: number;
          isShuffling?: boolean;
          isLooping?: boolean;
          isPlaybackFromPortfolio?: boolean;
          portfolioSessionId?: string;
        } = JSON.parse(dataStr);

        if (!data.currentItem) {
          return false; // return if currentItem is missing
        }

        if (
          data.playlist &&
          !data.playlist.items.find((item) => item.id === data.currentItem?.id)
        ) {
          return false; // return if playlist exists but current item is no longer part of it
        }

        // Check if portfolio privileges should be restored
        let shouldRestorePortfolioPrivileges = false;
        if (data.isPlaybackFromPortfolio && data.portfolioSessionId) {
          const currentSessionId = sessionStorage.getItem(portfolioSessionKey);
          // Only restore portfolio privileges if it's the same session
          shouldRestorePortfolioPrivileges = currentSessionId === data.portfolioSessionId;
        }

        if (data.currentItem) {
          if (audioRef.current) {
            return false; // return if player is already active
          }
          if (data.currentTime && !data.currentItem.isLivestream) {
            initialAudioPosition.current = data.currentTime;
            setCurrentTime(data.currentTime);
          }
          autoPlayOnLoad.current = false;
          // Note: We're loading cached data but fresh data from startPlayback() will override this
          setCurrentItem(data.currentItem);
          // only bottom player supports loading from local storage
          playEventSource.current = 'bottom_player';
        }
        if (data.playlist) {
          setPlaylist(data.playlist);
        }
        if (data.isShuffling) {
          setIsShuffling(data.isShuffling);
        }
        if (data.isLooping) {
          setIsLooping(data.isLooping);
        }
        // Only restore portfolio privileges if it's the same session
        setIsPlaybackFromPortfolio(shouldRestorePortfolioPrivileges);

        return true;
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      }
    }

    return false;
  }, [currentItem, syncInLocalStorage]);

  // load player data from local storage or load the default playback item if no data is found in local storage
  React.useEffect(() => {
    const loadedFromLocalStorage = loadLastPlaybackItem();
    const playerWasShown = localStorage.getItem(playerWasShownStorageKey) === 'true';
    // load the default playback item if the user is new and has not started a playback yet
    if (!loadedFromLocalStorage && !audioRef.current && defaultPlaybackItem && !playerWasShown) {
      autoPlayOnLoad.current = false;
      setCurrentItem(defaultPlaybackItem);
      // only bottom player adds default playback item on page load
      playEventSource.current = 'bottom_player';
      setIsPlaybackFromPortfolio(false); // Default items are never from portfolio
    }
  }, [currentItem, defaultPlaybackItem, loadLastPlaybackItem, syncInLocalStorage]);

  // save player data in local storage on page exit
  React.useEffect(() => {
    if (!syncInLocalStorage) {
      return () => {};
    }

    if (typeof window === 'undefined') {
      return () => {};
    }

    function savePlayerData() {
      try {
        // Get or create session ID for portfolio tracking
        let portfolioSessionId = sessionStorage.getItem(portfolioSessionKey);
        if (!portfolioSessionId) {
          portfolioSessionId = `portfolio-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
          sessionStorage.setItem(portfolioSessionKey, portfolioSessionId);
        }

        localStorage.setItem(
          playerDataStorageKey,
          JSON.stringify({
            playlist,
            currentItem,
            currentTime,
            isShuffling,
            isLooping,
            isPlaybackFromPortfolio,
            portfolioSessionId,
          }),
        );
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      }
    }

    // Save player data whenever the page is backgrounded or unloaded.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        savePlayerData();
      }
    };
    window.addEventListener('visibilitychange', handleVisibilityChange);

    // NOTE: Safari does not reliably fire the `visibilitychange` event when the
    // page is being unloaded.
    window.addEventListener('pagehide', savePlayerData);

    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', savePlayerData);
    };
  }, [
    currentItem,
    currentTime,
    isLooping,
    isShuffling,
    syncInLocalStorage,
    playlist,
    isPlaybackFromPortfolio,
  ]);

  // Creates or resumes the MediaStream <audio> element that feeds the GainNode output
  // to iOS's AVAudioSession. Always safe to call; creates on first call, resumes if paused.
  const ensureRadioAudioPlaying = React.useCallback(() => {
    if (!radioUnlockAudioRef.current && radioStreamDestRef.current) {
      const audio = new Audio();
      audio.srcObject = radioStreamDestRef.current.stream;
      audio.play().catch(() => {});
      radioUnlockAudioRef.current = audio;
    } else if (radioUnlockAudioRef.current?.paused) {
      radioUnlockAudioRef.current.play().catch(() => {});
    }
  }, []);

  const releaseRadioAudio = React.useCallback(() => {
    if (radioUnlockAudioRef.current) {
      radioUnlockAudioRef.current.pause();
      radioUnlockAudioRef.current.srcObject = null;
      radioUnlockAudioRef.current = null;
    }
  }, []);

  // Fires recording_play_start once per item. playStartEventLogged resets on every item change,
  // so playing the same recording again gets its own event but resuming after a pause does not.
  const logPlayStartOnce = React.useCallback(
    (item: PlaybackItem | null | undefined, startTime: number) => {
      if (!playEventSource.current || !item || playStartEventLogged.current) {
        return;
      }
      logPlayerEvent('recording_play_start', item.id, playEventSource.current, {
        startTime: Math.round(startTime),
      });
      playStartEventLogged.current = true;
    },
    [],
  );

  // Builds the AudioContext and GainNode a station plays through, once per player lifetime.
  const ensureRadioGraph = React.useCallback(() => {
    if (audioContextRef.current) {
      return audioContextRef.current;
    }

    const ctx = new AudioContext();
    const gain = ctx.createGain();
    audioContextRef.current = ctx;
    gainNodeRef.current = gain;

    // Route through MediaStreamDestinationNode so the <audio> element plays the real
    // station output — iOS keeps AVAudioSession alive for HTML audio elements.
    const streamDest = ctx.createMediaStreamDestination();
    radioStreamDestRef.current = streamDest;
    gain.connect(streamDest);

    return ctx;
  }, []);

  // Readies the Web Audio graph for a station that is about to become the current item.
  // iOS Safari requires AudioContext creation and resume() to happen synchronously inside the
  // user gesture handler. The radio setup effect runs after React re-renders (outside the
  // gesture), so by the time it creates the AudioContext it is already suspended and iOS will
  // silently refuse ctx.resume(). Every gesture that swaps the station — a play button, next,
  // previous — has to call this before setting the new item, or the switch keeps the previous
  // station's gain and buffered MediaStream packets and bursts them as the new one starts.
  const prepareRadioSwitch = React.useCallback(() => {
    const ctx = ensureRadioGraph();
    // When switching to a new station while the context is suspended (i.e. the previous
    // station was paused), stop the dormant source node now while the context is still
    // suspended — the stop is completely silent because the audio clock isn't running.
    // This prevents the effect cleanup from re-anchoring the gain at full volume and
    // producing a brief burst when the context resumes.
    if (ctx.state === 'suspended' && radioSourceNodeRef.current) {
      stopRadioSource(radioSourceNodeRef.current);
      radioSourceNodeRef.current = null;
    }
    // Zero gain before resuming the AudioContext. The old source node (if still connected
    // in the playing→switch path) is silenced during the async gap before the effect
    // cleanup runs on the next React commit. doStart() overwrites this with its own
    // setValueAtTime(0) + ramp, so nothing conflicts.
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.cancelScheduledValues(ctx.currentTime);
      gainNodeRef.current.gain.setValueAtTime(0, ctx.currentTime);
    }
    // Recreate the <audio> element to flush stale MediaStream buffer packets that were
    // queued before the gain was zeroed. Must be inside the user gesture for .play().
    if (!keepRadioAudioOnSwitchRef.current) {
      releaseRadioAudio();
    }
    ensureRadioAudioPlaying();
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
  }, [ensureRadioAudioPlaying, ensureRadioGraph, releaseRadioAudio]);

  const startPlayback: AudioPlayerInstance['startPlayback'] = React.useCallback(
    (params) => {
      if (params.playlist && !params.playlist.items.find((item) => item.id === params.item.id)) {
        throw new Error('Item not in playlist!');
      }

      autoPlayOnLoad.current = true;
      setCurrentItem(params.item);
      setPlaylist(params.playlist || null);

      if (playsThroughWebAudio(params.item)) {
        // Same station already playing — nothing to tear down or re-start.
        if (params.item.id === currentItemIdRef.current && shouldResumeRadioRef.current) {
          return;
        }
        prepareRadioSwitch();
      }

      // Store playlist and current item ID in refs as backup in case state gets cleared
      // Only store playlist if one is actually provided
      if (params.playlist) {
        playlistRef.current = params.playlist;
        currentItemIdRef.current = params.item?.id || null;
      } else {
        // Clear refs when playing single track to prevent using old playlist data
        playlistRef.current = null;
        currentItemIdRef.current = null;
      }
      playEventSource.current = params.eventSource || null;

      // Set portfolio state based on whether playback started from portfolio page
      setIsPlaybackFromPortfolio(isPortfolio);

      // If starting playback from portfolio, clear any existing preview mode
      if (isPortfolio) {
        setIsPreviewMode(false);
        setPreviewTimeRemaining(0);
        clearPreviewTimers();
      }

      // Create session ID when starting portfolio playback
      if (isPortfolio && typeof window !== 'undefined') {
        let portfolioSessionId = sessionStorage.getItem(portfolioSessionKey);
        if (!portfolioSessionId) {
          portfolioSessionId = `portfolio-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
          sessionStorage.setItem(portfolioSessionKey, portfolioSessionId);
        }
      }
    },
    [isPortfolio, clearPreviewTimers, prepareRadioSwitch],
  );

  const loadPlayback: AudioPlayerInstance['loadPlayback'] = React.useCallback((params) => {
    setCurrentItem(params.item);
    autoPlayOnLoad.current = false;
    playEventSource.current = params.eventSource || null;
  }, []);

  const hasNext = React.useMemo(() => {
    if (!playlist || playlist.items.length < 2 || !currentItem) {
      return false;
    }

    const currentItemIndex = playlist.items.findIndex((item) => item.id === currentItem.id);
    return currentItemIndex < playlist.items.length - 1;
  }, [currentItem, playlist]);

  const hasPrev = React.useMemo(() => {
    if (!playlist || playlist.items.length < 2 || !currentItem) {
      return false;
    }

    const currentItemIndex = playlist.items.findIndex((item) => item.id === currentItem.id);
    return currentItemIndex > 0;
  }, [currentItem, playlist]);

  const playPrevious = React.useCallback(() => {
    if (!playlist || playlist.items.length === 0 || !currentItem) {
      return;
    }

    const currentItemIndex = playlist.items.findIndex((item) => item.id === currentItem.id);
    const prevItemIndex = currentItemIndex > 0 ? currentItemIndex - 1 : playlist.items.length - 1;
    const prevItem = playlist.items[prevItemIndex];

    if (playsThroughWebAudio(prevItem) && prevItem.id !== currentItem.id) {
      prepareRadioSwitch();
    }

    setCurrentItem(prevItem);
    playEventSource.current = 'playlist_prev';
  }, [currentItem, playlist, prepareRadioSwitch]);

  const playNext = React.useCallback(() => {
    const playlistToUse = playlist || playlistRef.current;
    if (!playlistToUse || playlistToUse.items.length === 0) {
      return;
    }

    // Use current item ID from ref as fallback since currentItem state is often null
    const currentItemId = currentItem?.id || currentItemIdRef.current;
    const currentItemIndex = currentItemId
      ? playlistToUse.items.findIndex((item) => item.id === currentItemId)
      : 0;
    const nextItemIndex =
      currentItemIndex < playlistToUse.items.length - 1 ? currentItemIndex + 1 : 0;

    const nextItem = playlistToUse.items[nextItemIndex];

    if (playsThroughWebAudio(nextItem) && nextItem.id !== currentItemId) {
      prepareRadioSwitch();
    }

    setCurrentItem(nextItem);
    setPlaylist(playlistToUse); // Restore playlist state
    currentItemIdRef.current = nextItem?.id || null; // Update ref
    playEventSource.current = 'playlist_next';
  }, [currentItem, playlist, prepareRadioSwitch]);

  const playNextRandom = React.useCallback(() => {
    if (!playlist || playlist.items.length === 0) {
      return;
    }

    // if there is only one item in the list then just restart it
    if (playlist.items.length === 1) {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        safeAudioPlay(audioRef.current.play());
      }
      return;
    }

    // else randomly find next item which is different the current one
    let nextItem = arraySample(playlist.items);
    while (nextItem.id === currentItem?.id) {
      nextItem = arraySample(playlist.items);
    }

    if (playsThroughWebAudio(nextItem)) {
      prepareRadioSwitch();
    }

    setCurrentItem(nextItem);
    playEventSource.current = 'playlist_next_random';
  }, [currentItem?.id, playlist, prepareRadioSwitch]);

  // Once a preview has faded out, moves on to the next item unless the membership popup opened or
  // there is nothing to move on to. Returns whether it moved on.
  const moveOnAfterPreview = React.useCallback(() => {
    // Left on, a station's poller would cut the preview again the moment play is pressed.
    setIsPreviewMode(false);
    setPreviewTimeRemaining(0);

    if (showMembershipRef.current()) {
      return false;
    }

    // Use ref as fallback if state was cleared
    const playlistToUse = playlist || playlistRef.current;
    if (!playlistToUse || playlistToUse.items.length < 2) {
      return false;
    }

    const currentItemId = currentItem?.id || currentItemIdRef.current;
    const currentIndex = currentItemId
      ? playlistToUse.items.findIndex((item) => item.id === currentItemId)
      : 0;
    if (currentIndex === playlistToUse.items.length - 1 && !isLooping) {
      return false;
    }

    autoPlayOnLoad.current = true;
    // No tap is behind this switch, so iOS would refuse to start a rebuilt station element. The
    // current one can stay: the fade has left nothing audible in its buffer to flush.
    keepRadioAudioOnSwitchRef.current = true;
    if (isShuffling) {
      playNextRandom();
    } else {
      playNext();
    }
    keepRadioAudioOnSwitchRef.current = false;
    return true;
  }, [currentItem?.id, isLooping, isShuffling, playNext, playNextRandom, playlist]);

  const fadeOutAndStop = React.useCallback(() => {
    // Prevent multiple fade operations from running simultaneously
    if (fadeIntervalRef.current) return;

    // Radio path: fade via GainNode then suspend the AudioContext
    if (isWebAudioStation) {
      const gain = gainNodeRef.current;
      const ctx = audioContextRef.current;
      if (!gain || !ctx) return;

      const originalGain = gain.gain.value;
      let currentGain = originalGain;
      const fadeStep = originalGain / 20;

      fadeIntervalRef.current = setInterval(() => {
        currentGain -= fadeStep;
        if (currentGain <= 0) {
          gain.gain.value = 0;
          clearInterval(fadeIntervalRef.current!);
          fadeIntervalRef.current = null;

          if (moveOnAfterPreview()) {
            // Leaving 'playing' stops the poller, which still measures the old channel's time
            // and would otherwise cut the next channel's preview straight away.
            setState('buffering');
            return;
          }

          shouldResumeRadioRef.current = false; // same race as pause() — set before suspend()
          ctx.suspend().then(() => {
            setState('paused');
            // Restore gain inside .then() so it doesn't briefly un-zero the gain
            // while the context is still running (which would produce an audible pop).
            gain.gain.value = originalGain;
          });
          radioUnlockAudioRef.current?.pause();
          autoPlayOnLoad.current = false;
        } else {
          gain.gain.value = currentGain;
        }
      }, 50);
      return;
    }

    if (!audioRef.current) return;

    originalVolumeRef.current = audioRef.current.volume;
    let currentVolume = audioRef.current.volume;
    const fadeStep = currentVolume / 20; // Fade out over ~1 second (50ms * 20 steps)

    fadeIntervalRef.current = setInterval(() => {
      if (!audioRef.current) {
        clearInterval(fadeIntervalRef.current!);
        return;
      }

      currentVolume -= fadeStep;
      if (currentVolume <= 0) {
        audioRef.current.pause();
        audioRef.current.volume = originalVolumeRef.current; // Restore original volume
        clearInterval(fadeIntervalRef.current!);
        fadeIntervalRef.current = null;

        if (!moveOnAfterPreview()) {
          setState('paused');
          autoPlayOnLoad.current = false;
        }
      } else {
        audioRef.current.volume = currentVolume;
      }
    }, 50);
  }, [isWebAudioStation, moveOnAfterPreview]);

  const startPreviewMode = React.useCallback(() => {
    setIsPreviewMode(true);
  }, []);

  // Enforces the preview on premium content, whether playback has just started or membership was
  // lost mid-play.
  React.useEffect(() => {
    if (!shouldEnterPreviewMode || isPreviewMode) {
      return;
    }
    if (isWebAudioStation && state === 'playing') {
      startPreviewMode();
      return;
    }
    const audio = audioRef.current;
    if (audio && (state === 'playing' || !audio.paused)) {
      if (audio.currentTime >= 30) {
        fadeOutAndStop();
      } else {
        startPreviewMode();
      }
    }
  }, [
    state,
    shouldEnterPreviewMode,
    isPreviewMode,
    startPreviewMode,
    fadeOutAndStop,
    isWebAudioStation,
  ]);

  // Starts (or resumes) the Web Audio source node for the current radio station.
  // If the AudioContext is suspended (e.g. first play on iOS), it is resumed first.
  // If a source node already exists it means we are resuming from pause — only the
  // context needs resuming; the source keeps playing from where it stopped.
  const startRadio = React.useCallback(
    (item?: PlaybackItem | null) => {
      const ctx = audioContextRef.current;
      const gain = gainNodeRef.current;
      const buffer = radioBufferRef.current;
      if (!ctx || !gain || !buffer) return;

      if (radioSourceNodeRef.current) {
        // Already have a running source — just un-suspend the context
        ctx
          .resume()
          .then(() => setState('playing'))
          .catch(() => setState('paused'));
        return;
      }

      const doStart = () => {
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.loop = true;
        source.loopStart = radioLoopStartRef.current;
        source.loopEnd = radioLoopEndRef.current;
        source.connect(gain);
        // If a fade-out from the previous station is still in progress, delay the new
        // source until the fade-out finishes. cancelScheduledValues before that point
        // would abort the old ramp early and leave the gain at a non-zero value when
        // the old source's scheduled stop() fires — producing the buffered click burst.
        const startAt = Math.max(ctx.currentTime, radioFadeOutEndTimeRef.current);
        source.start(startAt, radioLoopStartRef.current);
        radioSourceNodeRef.current = source;
        radioPlayStartTimeRef.current = startAt;
        // Cancel any events AFTER the old fade-out ends (startAt+1ms keeps the ramp-to-0
        // at startAt intact), anchor gain to 0 at startAt, then ramp up.
        gain.gain.cancelScheduledValues(startAt + 0.001);
        gain.gain.setValueAtTime(0, startAt);
        gain.gain.linearRampToValueAtTime(userVolumeRef.current, startAt + 0.5);
        setState('playing');
        logPlayStartOnce(item, 0);
      };

      if (ctx.state === 'suspended') {
        ctx
          .resume()
          .then(doStart)
          .catch(() => setState('paused'));
      } else {
        doStart();
      }
    },
    [logPlayStartOnce],
  );

  const play = React.useCallback(() => {
    // Pause turned this off, so without it next and previous would load an item without playing it.
    autoPlayOnLoad.current = true;

    if (isStreamedStation) {
      const players = stationPlayersRef.current;
      if (players) {
        safeAudioPlay(players.active.play());
      }
      logPlayStartOnce(currentItem, 0);
      return;
    }

    if (isWebAudioStation) {
      ensureRadioAudioPlaying();
      if (radioBufferRef.current) {
        startRadio(currentItem);
      } else {
        // Buffer is still being fetched/decoded — unlock the AudioContext now while we
        // have a user gesture so startRadio() can start the node when decode finishes.
        if (audioContextRef.current?.state === 'suspended') {
          audioContextRef.current.resume();
        }
        setState('buffering');
      }
      return;
    }

    // If preview ended (currentTime >= 30) and user clicks play, move to next track
    if (
      shouldEnterPreviewMode &&
      audioRef.current &&
      audioRef.current.currentTime >= 30 &&
      playlist &&
      playlist.items.length > 1
    ) {
      const currentIndex = playlist.items.findIndex((item) => item.id === currentItem?.id);
      const isLastItem = currentIndex === playlist.items.length - 1;

      if (!isLastItem || isLooping) {
        if (isShuffling) {
          playNextRandom();
        } else {
          playNext();
        }
        return; // Exit early, let the next track auto-play
      }
    }

    // If user tries to play after preview ended but it's a single track, reset to beginning
    if (shouldEnterPreviewMode && audioRef.current && audioRef.current.currentTime >= 30) {
      audioRef.current.currentTime = 0;
    }

    safeAudioPlay(audioRef.current?.play());

    if (freeTimeLimitReachedRef.current && !isPlaybackFromPortfolio) {
      showMembershipRef.current('free-limit-reached');
      audioRef.current?.pause();
    }

    if (audioRef.current) {
      setCurrentlyPlayingItem(audioRef.current?.src);
    }

    // Start preview mode for non-premium users playing premium content
    if (shouldEnterPreviewMode && !isPreviewMode) {
      startPreviewMode();
    } else if (
      shouldEnterPreviewMode &&
      isPreviewMode &&
      audioRef.current &&
      audioRef.current.currentTime === 0
    ) {
      // Restart preview mode if playing from beginning
      startPreviewMode();
    }

    logPlayStartOnce(currentItem, currentTime);
  }, [
    logPlayStartOnce,
    currentItem,
    currentTime,
    isStreamedStation,
    isWebAudioStation,
    setCurrentlyPlayingItem,
    shouldEnterPreviewMode,
    isPreviewMode,
    startPreviewMode,
    playlist,
    isLooping,
    isShuffling,
    playNext,
    playNextRandom,
    isPlaybackFromPortfolio,
    startRadio,
    ensureRadioAudioPlaying,
  ]);

  React.useEffect(() => {
    playStartEventLogged.current = false;
  }, [currentItem]);

  // if a standalone audio player is playing other audio players pause
  React.useEffect(() => {
    // A station never announces itself here, so an announcement is always another player's. It
    // stops through pause(): on Web Audio audioRef holds nothing, and on Android only half a pair.
    if (isStationRef.current) {
      pauseRef.current();
      return;
    }

    const isCurrentAudioPlayerPlaying =
      audioRef.current && currentlyPlayingItem === audioRef.current.src;

    if (!isCurrentAudioPlayerPlaying) {
      audioRef.current?.pause();
    }
  }, [setCurrentlyPlayingItem, currentlyPlayingItem]);

  React.useEffect(() => {
    if (isStandaloneAudioPlayerPlaying) {
      return () => {
        audioRef.current?.pause();
      };
    }
    return () => {};
  }, [isStandaloneAudioPlayerPlaying]);

  // Stops a streamed station, whether the pause button asked or something else paused the audible
  // player, such as another app taking over the audio.
  const stopStationPlayers = React.useCallback(() => {
    const players = stationPlayersRef.current;
    if (stationFadeRef.current && players) {
      // Mid-join: end it now with the incoming player as the audible one, so resuming does
      // not bring back a player that was already fading away.
      clearInterval(stationFadeRef.current);
      stationFadeRef.current = null;
      players.active.currentTime = stationLoopHeadSkip;
      players.active.volume = 0;
      players.standby.volume = userVolumeRef.current;
      stationPlayersRef.current = { active: players.standby, standby: players.active };
      audioRef.current = players.standby;
    }
    stationPlayersRef.current?.active.pause();
    stationPlayersRef.current?.standby.pause();
    setState('paused');
  }, []);

  const pause = React.useCallback(() => {
    if (isStreamedStation) {
      autoPlayOnLoad.current = false;
      stopStationPlayers();
      return;
    }

    if (isWebAudioStation) {
      // Otherwise a channel paused while still decoding starts playing when decoding finishes.
      autoPlayOnLoad.current = false;
      // Set ref synchronously before suspend() — ctx fires 'statechange' immediately on
      // suspend, before any React state update, so the handler would see the old ref value
      // (true) and call resume(), fighting the pause. Setting it here prevents that race.
      shouldResumeRadioRef.current = false;
      if (audioContextRef.current) {
        audioContextRef.current
          .suspend()
          .then(() => setState('paused'))
          .catch(() => {});
      } else {
        setState('paused');
      }
      radioUnlockAudioRef.current?.pause();
      return;
    }
    autoPlayOnLoad.current = false;
    try {
      audioRef.current?.pause();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }
  }, [isStreamedStation, isWebAudioStation, stopStationPlayers]);
  pauseRef.current = pause;

  const reset = React.useCallback(() => {
    // Stop radio Web Audio immediately — don't wait for the setup effect cleanup
    stopRadioSource(radioSourceNodeRef.current);
    radioSourceNodeRef.current = null;
    radioBufferRef.current = null;
    releaseRadioAudio();

    setState('idle');
    setCurrentItem(null);
    playEventSource.current = null;
    setPlaylist(null);
    setCurrentTime(0);
    setIsPlaybackFromPortfolio(false);
  }, [releaseRadioAudio]);

  const seekTo = React.useCallback(
    (time: number) => {
      if (!audioRef.current || typeof time !== 'number') {
        return;
      }

      // In preview mode, prevent seeking beyond 30 seconds to avoid triggering fadeOutAndStop
      if (shouldEnterPreviewMode && time >= 30) {
        audioRef.current.currentTime = 29.8;
        return;
      }

      audioRef.current.currentTime = time;

      // Restore preview mode when seeking back within the preview window
      if (shouldEnterPreviewMode && !isPreviewMode && time < 30) {
        setIsPreviewMode(true);
      }
    },
    [shouldEnterPreviewMode, isPreviewMode],
  );

  const toggleIsShuffling = React.useCallback(() => {
    // Disable shuffle toggle when in preview mode
    if (isPreviewMode) {
      return;
    }
    setIsShuffling((shuffling) => !shuffling);
  }, [isPreviewMode]);

  const toggleIsLooping = React.useCallback(() => {
    // Disable repeat toggle when in preview mode
    if (isPreviewMode) {
      return;
    }
    setIsLooping((looping) => !looping);
  }, [isPreviewMode]);

  // Fetch, decode, and prepare the Web Audio buffer for a radio station.
  // Runs whenever the radio item changes. Parses LAME gapless metadata before
  // calling decodeAudioData because the browser detaches the ArrayBuffer after decode.
  React.useEffect(() => {
    if (!isWebAudioStation || !currentItem?.sourceUrl) return undefined;

    // Only show buffering immediately if we're going to auto-play. On a localStorage
    // restore (autoPlayOnLoad = false) stay quiet until decode finishes so the card
    // doesn't flash as active before settling to paused.
    if (autoPlayOnLoad.current) {
      setState('buffering');
    }

    // startRadio() can create a source after the previous cleanup has run, when the context
    // finishes resuming only once the channel has already been switched.
    stopRadioSource(radioSourceNodeRef.current);
    radioSourceNodeRef.current = null;
    radioBufferRef.current = null;

    const resolvedUrl = resolveAudioSourceUrl(currentItem.sourceUrl);

    // Normally created in prepareRadioSwitch() while inside the user gesture; this call only
    // builds the graph if the effect got here first (e.g. localStorage restore).
    const ctx = ensureRadioGraph();
    let cancelled = false;

    fetch(resolvedUrl)
      .then((res) => res.arrayBuffer())
      .then(async (arrayBuffer) => {
        if (cancelled) return;

        // Parse LAME header BEFORE decodeAudioData — the browser detaches the
        // ArrayBuffer during decode so it can no longer be read afterwards.
        const lameInfo = parseLameHeader(arrayBuffer);
        // Use the callback-based overload — the Promise-based API on iOS WebKit
        // hangs indefinitely when the AudioContext is suspended, which it may still
        // be at this point even though resume() was called in the user gesture.
        const audioBuffer = await new Promise<AudioBuffer>((resolve, reject) => {
          ctx.decodeAudioData(arrayBuffer, resolve, reject);
        });
        if (cancelled) return;

        radioBufferRef.current = audioBuffer;
        radioLoopStartRef.current = lameInfo ? lameInfo.encoderDelay / audioBuffer.sampleRate : 0;
        radioLoopEndRef.current = lameInfo
          ? audioBuffer.duration - lameInfo.encoderPadding / audioBuffer.sampleRate
          : audioBuffer.duration;

        if (autoPlayOnLoad.current) {
          startRadio(currentItem);
        } else {
          setState('paused');
        }
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });

    return () => {
      cancelled = true;
      // If a membership fade-out interval is still running for the old station, cancel it.
      // Without this, its final tick can suspend the AudioContext after the new station
      // has already started, or set autoPlayOnLoad=false and suppress the new station.
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      }
      const source = radioSourceNodeRef.current;
      const gain = gainNodeRef.current;
      const cleanupCtx = audioContextRef.current;
      if (source && gain && cleanupCtx) {
        // Schedule a short fade-out before stopping so the waveform isn't cut mid-sample.
        // An abrupt stop() produces a click that the MediaStream buffer can stretch into
        // a longer artifact. 50ms is inaudible as a crossfade but eliminates the click.
        const stopTime = cleanupCtx.currentTime + 0.05;
        radioFadeOutEndTimeRef.current = stopTime; // startRadio() waits until this time
        // Hold the automation value rather than reading gain.gain.value: browsers return the
        // intrinsic value there, not the 0 a switch has just scheduled, so the ramp would
        // start from full volume and burst the outgoing station before it stops.
        if (typeof gain.gain.cancelAndHoldAtTime === 'function') {
          gain.gain.cancelAndHoldAtTime(cleanupCtx.currentTime);
        } else {
          gain.gain.cancelScheduledValues(cleanupCtx.currentTime);
          gain.gain.setValueAtTime(gain.gain.value, cleanupCtx.currentTime);
        }
        gain.gain.linearRampToValueAtTime(0, stopTime);
        stopRadioSource(source, stopTime);
      } else {
        stopRadioSource(source);
      }
      radioSourceNodeRef.current = null;
      radioBufferRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem?.id, isWebAudioStation]);

  React.useEffect(() => {
    if (!currentItem?.sourceUrl) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = undefined;
      }
      return undefined;
    }

    // Stations play through their own path, so stop any in-progress HTML audio (e.g. switching
    // from a normal recording) then skip HTML audio setup. A streamed station's audible player is
    // mirrored into audioRef and must survive this effect re-running on an unrelated change, such
    // as preview mode switching on as it starts.
    if (currentItem.isRadio) {
      const players = stationPlayersRef.current;
      const isStationPlayer =
        audioRef.current === players?.active || audioRef.current === players?.standby;
      if (audioRef.current && !isStationPlayer) {
        audioRef.current.pause();
        audioRef.current = undefined;
      }
      return undefined;
    }

    // Switching from radio to a normal recording: stop the MediaStream <audio> element
    // so it doesn't conflict with the normal recording's audio session on iOS.
    releaseRadioAudio();

    // Check if currentItem actually changed (not just other dependencies like membership)
    // On initial mount, prevCurrentItemRef.current is null, so treat as "changed"
    const currentItemChanged =
      prevCurrentItemRef.current === null || prevCurrentItemRef.current?.id !== currentItem?.id;

    const resolvedSourceUrl = resolveAudioSourceUrl(currentItem.sourceUrl);

    if (!audioRef.current) {
      audioRef.current = new Audio(resolvedSourceUrl);
    }

    const audio = audioRef.current;

    // Only update audio source if currentItem changed to avoid triggering reload on membership changes
    if (currentItemChanged && audio.src !== resolvedSourceUrl) {
      audio.src = resolvedSourceUrl;
    }

    const handleLoading = () => {
      setState('buffering');
    };

    const handleWaiting = () => {
      setState('buffering');
    };

    const handlePlaying = () => {
      setState('playing');
    };

    const handlePause = () => {
      setState('paused');
    };

    const handleTimeupdate = () => {
      const currentAudioTime = audioRef.current?.currentTime || 0;
      setCurrentTime(currentAudioTime);

      // In preview mode, check if we've reached 30 seconds and fade out
      // Only trigger if audio is still playing and not already fading.
      // Skip while seeking — currentTime briefly reports the old position (>= 30) even
      // after audio.currentTime = 0 is set, because the seek is asynchronous.
      if (
        shouldEnterPreviewMode &&
        isPreviewMode &&
        currentAudioTime >= 30 &&
        !audioRef.current?.paused &&
        !fadeIntervalRef.current &&
        !audioRef.current?.seeking
      ) {
        fadeOutAndStop();
      }

      // Calculate remaining preview time based on actual position
      if (shouldEnterPreviewMode && isPreviewMode && currentAudioTime < 30) {
        const remaining = Math.max(0, Math.ceil(30 - currentAudioTime));
        setPreviewTimeRemaining(remaining);
      }
    };

    const handleError = () => {
      setState('error');
    };

    const handleEnded = () => {
      if (playlist) {
        const currentIndex = playlist.items.findIndex(({ id }) => currentItem.id === id);
        const isLastItem =
          playlist.items.length > 1 ? currentIndex === playlist.items.length - 1 : true;

        if (!isLastItem || isLooping) {
          if (isShuffling) {
            playNextRandom();
          } else {
            playNext();
          }
        }
      } else if (isLooping && audioRef.current) {
        audioRef.current.currentTime = 0;
        safeAudioPlay(audioRef.current.play());
      } else {
        setState('idle');
      }
      if (!allRecordingsFree) {
        showMembershipRef.current();
      }
    };

    audio.addEventListener('loading', handleLoading);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('timeupdate', handleTimeupdate);
    audio.addEventListener('error', handleError);
    audio.addEventListener('ended', handleEnded);

    // Only auto-play if currentItem actually changed and autoPlayOnLoad is true
    if (autoPlayOnLoad.current && currentItemChanged) {
      safeAudioPlay(audio.play());

      if (freeTimeLimitReachedRef.current && !isPlaybackFromPortfolio) {
        showMembershipRef.current('free-limit-reached');
        audioRef.current?.pause();
      }

      // Start preview mode for non-premium users playing premium content
      if (shouldEnterPreviewMode && !isPreviewMode) {
        startPreviewMode();
      }

      logPlayStartOnce(currentItem, initialAudioPosition.current);
    }

    if (initialAudioPosition.current > 0) {
      audio.currentTime = initialAudioPosition.current;
    }

    initialAudioPosition.current = 0; // reset initialAudioPosition after first use

    // Only reset autoPlayOnLoad when currentItem actually changes, not when other dependencies change
    if (currentItemChanged) {
      autoPlayOnLoad.current = true; // reset autoPlayOnLoad after first use
      prevCurrentItemRef.current = currentItem;
    }

    return () => {
      audio.removeEventListener('loading', handleLoading);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('timeupdate', handleTimeupdate);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [
    currentItem,
    releaseRadioAudio,
    fadeOutAndStop,
    isLooping,
    logPlayStartOnce,
    isPreviewMode,
    isShuffling,
    membership,
    playNext,
    playNextRandom,
    playlist,
    shouldEnterPreviewMode,
    startPreviewMode,
    isPlaybackFromPortfolio,
  ]);

  // Refs kept current each render so the polling interval always reads the latest values
  // without being restarted every time they change.
  const shouldEnterPreviewModeRef = React.useRef(shouldEnterPreviewMode);
  shouldEnterPreviewModeRef.current = shouldEnterPreviewMode;
  const isPreviewModeRef = React.useRef(isPreviewMode);
  isPreviewModeRef.current = isPreviewMode;
  const fadeOutAndStopRef = React.useRef(fadeOutAndStop);
  fadeOutAndStopRef.current = fadeOutAndStop;

  // The 30 second preview a non-member gets on a premium channel, checked by both station pollers
  // with time actually heard, so pausing and resuming carries on rather than starting again.
  const applyStationPreviewWindow = React.useCallback((elapsed: number) => {
    if (!shouldEnterPreviewModeRef.current || !isPreviewModeRef.current) {
      return;
    }
    if (elapsed >= 30) {
      if (!fadeIntervalRef.current) {
        fadeOutAndStopRef.current();
      }
      return;
    }
    setPreviewTimeRemaining(Math.max(0, Math.ceil(30 - elapsed)));
  }, []);

  // Points a paused player at the in-memory copy, so its next turn costs no network.
  const adoptStationBlob = React.useCallback((role: 'active' | 'standby') => {
    const players = stationPlayersRef.current;
    const blobUrl = stationBlobUrlRef.current;
    if (!players || !blobUrl) {
      return;
    }
    const player = players[role];
    if (!player.paused || player.src === blobUrl) {
      return;
    }
    player.src = blobUrl;
    player.currentTime = stationLoopHeadSkip;
  }, []);

  // Keeps the channel after the first loop has read it through anyway. Someone who plays a
  // channel briefly never pays for this; someone who leaves it running stops paying entirely.
  const cacheStationAudio = React.useCallback(() => {
    if (!stationSourceUrlRef.current || stationBlobUrlRef.current || stationBlobAbortRef.current) {
      return;
    }

    const controller = new AbortController();
    stationBlobAbortRef.current = controller;

    fetch(stationSourceUrlRef.current, { signal: controller.signal })
      .then((response) => {
        // An error reply is still a reply; without this its body would be kept as the audio.
        if (!response.ok) {
          throw new Error(`Station audio request failed with ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        stationBlobUrlRef.current = URL.createObjectURL(blob);
        if (!stationFadeRef.current) {
          adoptStationBlob('standby');
        }
      })
      .catch(() => {
        // Left on the network URL: looping still works, it just keeps re-reading.
      });
  }, [adoptStationBlob]);

  // Hands playback over from the audible player to the one waiting at the start of the file.
  const crossfadeStationPlayers = React.useCallback(() => {
    const players = stationPlayersRef.current;
    if (!players || stationFadeRef.current) {
      return;
    }

    const { active, standby } = players;
    const target = userVolumeRef.current;
    cacheStationAudio();
    if (!stationStandbyReadyRef.current) {
      standby.currentTime = stationLoopHeadSkip;
    }
    standby.volume = 0;
    safeAudioPlay(standby.play());

    let step = 0;
    stationFadeRef.current = setInterval(
      () => {
        step += 1;
        const progress = Math.min(1, step / stationLoopFadeSteps);
        // Equal power, so the two players sum to a steady level through the join instead of
        // dipping in the middle the way two linear ramps would.
        standby.volume = Math.min(1, target * Math.sin((progress * Math.PI) / 2));
        active.volume = Math.min(1, target * Math.cos((progress * Math.PI) / 2));

        if (progress < 1) {
          return;
        }

        clearInterval(stationFadeRef.current!);
        stationFadeRef.current = null;
        // The incoming player can fail to start if it was still buffering. Ask again rather
        // than hand it the audible role while it is silent.
        if (standby.paused) {
          safeAudioPlay(standby.play());
        }
        active.pause();
        active.currentTime = stationLoopHeadSkip;
        active.volume = 0;
        adoptStationBlob('active');
        standby.volume = target;
        stationStandbyReadyRef.current = true; // rewound to the head just above
        stationStandbyRetriedRef.current = false;
        stationPlayersRef.current = { active: standby, standby: active };
        audioRef.current = standby;
      },
      (stationLoopCrossfade * 1000) / stationLoopFadeSteps,
    );
  }, [adoptStationBlob, cacheStationAudio]);

  // Builds the pair of players a streamed station loops through.
  React.useEffect(() => {
    if (!isStreamedStation || !currentItem?.sourceUrl) {
      return undefined;
    }

    const url = resolveAudioSourceUrl(currentItem.sourceUrl);
    stationSourceUrlRef.current = url;
    const active = new Audio(url);
    const standby = new Audio(url);
    const players = [active, standby];
    active.preload = 'auto';
    standby.preload = 'auto';
    active.volume = userVolumeRef.current;
    standby.volume = 0;
    stationPlayersRef.current = { active, standby };
    stationStandbyReadyRef.current = false;
    stationStandbyRetriedRef.current = false;
    audioRef.current = active;

    const handlePlaying = (event: Event) => {
      if (event.target === stationPlayersRef.current?.active) {
        setState('playing');
      }
    };
    const handleWaiting = (event: Event) => {
      if (event.target === stationPlayersRef.current?.active) {
        setState('buffering');
      }
    };
    // The loop's own handover pauses the outgoing player only after the roles have swapped, so it
    // never matches here.
    const handlePause = (event: Event) => {
      if (event.target === stationPlayersRef.current?.active) {
        stopStationPlayers();
      }
    };
    // Only the audible player failing means the station has failed. An idle one is reloaded from
    // the network, once per loop, so the next join has something to hand over to.
    const handleError = (event: Event) => {
      const pair = stationPlayersRef.current;
      if (!pair || event.target === pair.active) {
        setState('error');
        return;
      }
      if (stationStandbyRetriedRef.current) {
        return;
      }
      stationStandbyRetriedRef.current = true;
      pair.standby.src = stationSourceUrlRef.current;
      pair.standby.currentTime = stationLoopHeadSkip;
      stationStandbyReadyRef.current = true;
    };
    players.forEach((player) => {
      player.addEventListener('playing', handlePlaying);
      player.addEventListener('waiting', handleWaiting);
      player.addEventListener('pause', handlePause);
      player.addEventListener('error', handleError);
    });

    if (autoPlayOnLoad.current) {
      setState('buffering');
      active.currentTime = stationLoopHeadSkip;
      safeAudioPlay(active.play());

      if (shouldEnterPreviewMode && !isPreviewMode) {
        startPreviewMode();
      }
      logPlayStartOnce(currentItem, 0);
    } else {
      setState('paused');
    }

    return () => {
      if (stationFadeRef.current) {
        clearInterval(stationFadeRef.current);
        stationFadeRef.current = null;
      }
      players.forEach((player) => {
        player.removeEventListener('playing', handlePlaying);
        player.removeEventListener('waiting', handleWaiting);
        player.removeEventListener('pause', handlePause);
        player.removeEventListener('error', handleError);
        player.pause();
        player.removeAttribute('src');
        player.load();
      });
      stationPlayersRef.current = null;
      stationBlobAbortRef.current?.abort();
      stationBlobAbortRef.current = null;
      if (stationBlobUrlRef.current) {
        URL.revokeObjectURL(stationBlobUrlRef.current);
        stationBlobUrlRef.current = null;
      }
      if (audioRef.current === active || audioRef.current === standby) {
        audioRef.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem?.id, isStreamedStation]);

  // Drives a streamed station: position for the UI, the preview cut-off, and the loop itself.
  React.useEffect(() => {
    if (!isStreamedStation || state !== 'playing') {
      return undefined;
    }

    const interval = setInterval(() => {
      const players = stationPlayersRef.current;
      if (!players) {
        return;
      }

      const { active, standby } = players;
      setCurrentTime(active.currentTime);

      // The playhead, as ordinary recordings use: it stops while paused, and every channel is far
      // longer than the preview, so no loop join can reset it inside the window.
      applyStationPreviewWindow(active.currentTime);

      if (!Number.isFinite(active.duration) || stationFadeRef.current) {
        return;
      }

      const remaining = active.duration - stationLoopTailSkip - active.currentTime;
      if (!stationStandbyReadyRef.current && remaining <= stationLoopPrepareLead) {
        // Seeking now lets the browser buffer the start of the file while the other player is
        // still going, so the handover costs nothing but a volume change.
        standby.currentTime = stationLoopHeadSkip;
        stationStandbyReadyRef.current = true;
      }
      // Allowing for a tick keeps the fade from running past the point it was aimed at, which
      // would take it into the tail the skip exists to avoid.
      if (remaining <= stationLoopCrossfade + stationLoopPollInterval / 1000) {
        crossfadeStationPlayers();
      }
    }, stationLoopPollInterval);

    return () => clearInterval(interval);
  }, [isStreamedStation, state, crossfadeStationPlayers, applyStationPreviewWindow]);

  // Update currentTime for Web Audio stations — the HTML audio timeupdate event doesn't fire
  // for Web Audio, so we poll the AudioContext clock instead.
  React.useEffect(() => {
    if (!isWebAudioStation || state !== 'playing') return undefined;

    const interval = setInterval(() => {
      const ctx = audioContextRef.current;
      if (!ctx) return;
      const elapsed = Math.max(0, ctx.currentTime - radioPlayStartTimeRef.current);
      const loopDuration = radioLoopEndRef.current - radioLoopStartRef.current;
      if (loopDuration > 0) {
        setCurrentTime(radioLoopStartRef.current + (elapsed % loopDuration));
      }

      applyStationPreviewWindow(elapsed);
    }, stationLoopPollInterval);

    return () => clearInterval(interval);
  }, [isWebAudioStation, state, applyStationPreviewWindow]);

  React.useEffect(() => {
    if (typeof window !== 'undefined' && 'mediaSession' in window.navigator) {
      if (currentItem) {
        let mediaArtist: string;
        if (currentItem.isRadio) {
          mediaArtist = 'station';
        } else if (currentItem.recordists) {
          mediaArtist = currentItem.recordists.map((recordist) => recordist.title).join(', ');
        } else {
          mediaArtist = currentItem.recordist || '';
        }
        navigator.mediaSession.metadata = new MediaMetadata({
          title: currentItem.userFriendlyTitle || currentItem.title,
          artist: mediaArtist,
        });
      } else {
        navigator.mediaSession.metadata = null;
      }
    }
  }, [currentItem]);

  React.useEffect(() => {
    if (typeof window !== 'undefined' && currentItem) {
      localStorage.setItem(playerWasShownStorageKey, 'true');
    }
  }, [currentItem]);

  // Keep the ref in sync so event handlers below always read the current play state.
  React.useEffect(() => {
    shouldResumeRadioRef.current = state === 'playing' && isWebAudioStation;
  }, [state, isWebAudioStation]);

  // Registered once — reads current state via ref so it never needs to be re-added.
  // Fires on both screen lock (try to race iOS suspension) and unlock (restore if suspended).
  React.useEffect(() => {
    if (typeof window === 'undefined') return () => {};
    const handle = () => {
      if (!shouldResumeRadioRef.current) return;
      ensureRadioAudioPlaying();
      audioContextRef.current?.resume().catch(() => {});
    };
    document.addEventListener('visibilitychange', handle);
    return () => document.removeEventListener('visibilitychange', handle);
  }, [ensureRadioAudioPlaying]);

  // Event-driven: AudioContext fires 'statechange' the moment iOS suspends it on screen
  // lock — we call resume() immediately. shouldResumeRadioRef is false after a deliberate
  // pause, so the handler is a no-op then. Only re-registered when playback moves onto or off a
  // Web Audio station.
  React.useEffect(() => {
    const ctx = audioContextRef.current;
    if (!ctx || !isWebAudioStation) return () => {};
    const handle = () => {
      if (ctx.state === 'suspended' && shouldResumeRadioRef.current) {
        ensureRadioAudioPlaying();
        ctx.resume().catch(() => {});
      }
    };
    ctx.addEventListener('statechange', handle);
    return () => ctx.removeEventListener('statechange', handle);
  }, [isWebAudioStation, ensureRadioAudioPlaying]);

  // Keep the lock-screen playback state indicator in sync
  React.useEffect(() => {
    if (typeof window === 'undefined' || !('mediaSession' in navigator)) return;
    let playbackState: MediaSessionPlaybackState;
    if (state === 'playing') {
      playbackState = 'playing';
    } else if (state === 'paused') {
      playbackState = 'paused';
    } else {
      playbackState = 'none';
    }
    try {
      navigator.mediaSession.playbackState = playbackState;
    } catch {
      // playbackState not supported in this browser
    }
  }, [state]);

  // For stations: signal an endless stream so the lock screen shows LIVE instead of a duration.
  React.useEffect(() => {
    if (typeof window === 'undefined' || !('mediaSession' in navigator)) return;
    if (typeof navigator.mediaSession.setPositionState !== 'function') return;
    try {
      if (currentItem?.isRadio) {
        navigator.mediaSession.setPositionState({
          duration: Infinity,
          playbackRate: 1,
          position: 0,
        });
      } else {
        navigator.mediaSession.setPositionState();
      }
    } catch {
      // setPositionState with Infinity duration not supported in all browsers
    }
  }, [currentItem?.isRadio, currentItem?.id]);

  // Wire up lock-screen play/pause controls via MediaSession action handlers
  React.useEffect(() => {
    if (typeof window === 'undefined' || !('mediaSession' in navigator) || !currentItem)
      return () => {};
    navigator.mediaSession.setActionHandler('play', play);
    navigator.mediaSession.setActionHandler('pause', pause);
    if (currentItem.isRadio) {
      navigator.mediaSession.setActionHandler('seekbackward', null);
      navigator.mediaSession.setActionHandler('seekforward', null);
      try {
        navigator.mediaSession.setActionHandler('seekto', null);
      } catch {
        // 'seekto' not supported in all browsers
      }
    }
    return () => {
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
    };
  }, [currentItem, play, pause]);

  // Cleanup preview timers when currentItem changes or component unmounts
  React.useEffect(() => clearPreviewTimers(), [currentItem, clearPreviewTimers]);

  // Reset preview mode when currentItem changes
  React.useEffect(() => {
    clearPreviewTimers();
    setIsPreviewMode(false);
    setPreviewTimeRemaining(0);
  }, [currentItem, clearPreviewTimers]);

  return {
    audioRef,
    gainNodeRef,
    userVolumeRef,
    state,
    playlist,
    currentItem,
    hasNext,
    hasPrev,
    currentTime,
    isShuffling,
    isLooping,
    isPreviewMode,
    previewTimeRemaining,
    isPlaybackFromPortfolio,
    startPlayback,
    loadPlayback,
    play,
    pause,
    reset,
    seekTo: useDebounceCallback(seekTo),
    playPrevious,
    playNext,
    toggleIsShuffling,
    toggleIsLooping,
  };
}
